import React, { useState } from "react";
import { IoArrowBack, IoChevronDown, IoAdd, IoClose, IoReorderThree } from "react-icons/io5";
import WhatsappPreview from "./WhtsappPreview";
import CustomNavbar from "../../components/common/CustomNavbar";
import PageLayout from "../../components/layouts/PageLayout";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../store";
import { toast } from "sonner";
import { createWhatsappTemplate, uploadWhatsappMedia } from "../../features/template/TemplateService";
import PhoneInput from 'react-phone-number-input';
import { createGalleryPost } from "../../features/gallery/GalleryServices";
import { uploadFile } from "../../util/imageUpload";
import { sanitizeInput } from "../../util/functions/UniversalFunctions";
import validator from "validator";

interface Button {
  id: string;
  type: "Quick Reply" | "URL" | "Call to Action";
  text: string;
  url?: string;
  phoneNumber?: string;
}

interface TemplateData {
  templateName: string;
  category: string;
  templateType: string;
  language: string;
  headerType: string;
  headerText: string;
  bodyText: string;
  footerText: string;
  mediaHandle?: string;
}

export default function AddTemplate() {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [loading, setLoading] = useState(false);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [mediaFile, setMediaFile] = useState<File | null>(null);
  const [mediaPreviewUrl, setMediaPreviewUrl] = useState<string>("");
  const [uploadedMediaUrl, setUploadedMediaUrl] = useState("");

  const [templateData, setTemplateData] = useState<TemplateData>({
    templateName: "",
    category: "",
    templateType: "",
    language: "",
    headerType: "",
    headerText: "",
    bodyText: "",
    footerText: "",
    mediaHandle: "",
  });

  const [buttons, setButtons] = useState<Button[]>([]);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [buttonErrors, setButtonErrors] = useState<Record<string, string>>({});

  const handleInputChange = (field: keyof TemplateData, value: any) => {
    if (
      typeof value === "string" &&
      ["headerText", "bodyText", "footerText"].includes(field)
    ) {
      const { sanitizedValue, hasScript } = sanitizeInput(value);

      if (hasScript) {
        toast.error("Scripts or malicious code are not allowed");
      }

      setTemplateData((prev) => ({
        ...prev,
        [field]: sanitizedValue,
      }));

      return;
    }

    setTemplateData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleTemplateNameChange = (value: string) => {
    const formattedValue = value
      .toLowerCase()
      .replace(/\s+/g, "_")
      .replace(/[^a-z0-9_]/g, "");
    handleInputChange("templateName", formattedValue);
  };

  const handleHeaderTypeChange = (value: string) => {
    handleInputChange("headerType", value);
    // Reset media fields when header type changes
    setMediaFile(null);
    setMediaPreviewUrl("");
    handleInputChange("mediaHandle", "");
    handleInputChange("headerText", "");
  };

  const handleMediaUpload = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const MAX_FILE_SIZE_MB = 80;
    const fileSizeMB = file.size / (1024 * 1024);

    if (fileSizeMB > MAX_FILE_SIZE_MB) {
      // clear previous selected file also
      setMediaFile(null);
      setMediaPreviewUrl("");
      setUploadedMediaUrl("");
      handleInputChange("mediaHandle", "");

      // reset input value
      e.target.value = "";

      toast.error(`File size should be less than ${MAX_FILE_SIZE_MB}MB`);
      return;
    }

    const localPreview = URL.createObjectURL(file);

    // set only after validation success
    setMediaFile(file);
    setMediaPreviewUrl(localPreview);
    setUploadingMedia(true);

    try {
      // for template
      const { headerHandle } = await uploadWhatsappMedia(file);
      handleInputChange("mediaHandle", headerHandle);

      // for gallery
      const url = await uploadFile(file);
      setUploadedMediaUrl(url);

      toast.success("Media uploaded successfully");
    } catch (err: any) {
      toast.error("Media upload failed");
      setMediaFile(null);
      setMediaPreviewUrl("");
      setUploadedMediaUrl("");
      handleInputChange("mediaHandle", "");
    } finally {
      setUploadingMedia(false);
    }
  };

  const addButton = () => {
    if (buttons.length >= 3) {
      toast.error("Maximum 3 buttons allowed");
      return;
    }
    const newButton: Button = {
      id: Date.now().toString(),
      type: "Quick Reply",
      text: "",
    };
    setButtons([...buttons, newButton]);
  };

  const removeButton = (id: string) => {
    setButtons(buttons.filter(btn => btn.id !== id));
  };

  const updateButton = (id: string, field: keyof Button, value: any) => {
    let finalValue = value;

    if (
      typeof value === "string" &&
      ["text", "url"].includes(field)
    ) {
      const { sanitizedValue, hasScript } = sanitizeInput(value);

      if (hasScript) {
        toast.error("Scripts or malicious code are not allowed");
      }

      finalValue = sanitizedValue;

      // URL validation
      if (field === "url") {
        const valid =
          !finalValue ||
          validator.isURL(finalValue, {
            protocols: ["http", "https"],
            require_protocol: true,
          });

        setButtonErrors((prev) => ({
          ...prev,
          [id]: valid ? "" : "Enter a valid URL",
        }));
      }
    }

    setButtons(
      buttons.map((btn) =>
        btn.id === id
          ? { ...btn, [field]: finalValue }
          : btn
      )
    );
  };

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const newButtons = [...buttons];
    const draggedButton = newButtons[draggedIndex];
    newButtons.splice(draggedIndex, 1);
    newButtons.splice(index, 0, draggedButton);

    setButtons(newButtons);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const validateTemplate = () => {
    if (!templateData.templateName.trim()) {
      toast.error("Template name is required");
      return false;
    }
    if (!templateData.category) {
      toast.error("Category is required");
      return false;
    }
    if (!templateData.language) {
      toast.error("Language is required");
      return false;
    }
    if (!templateData.bodyText.trim()) {
      toast.error("Body text is required");
      return false;
    }
    if (templateData.headerType === "Text" && !templateData.headerText.trim()) {
      toast.error("Header text is required when Text Header is selected");
      return false;
    }
    if (
      ["Image", "Video", "Document"].includes(templateData.headerType) &&
      !templateData.mediaHandle
    ) {
      toast.error(`Please upload a ${templateData.headerType} for the header`);
      return false;
    }

    for (let i = 0; i < buttons.length; i++) {
      const btn = buttons[i];
      if (!btn.text.trim()) {
        toast.error(`Button ${i + 1} text is required`);
        return false;
      }
      if (btn.type === "URL") {
        if (!btn.url?.trim()) {
          toast.error(`Button ${i + 1} URL is required`);
          return false;
        }
        const isValidUrl = validator.isURL(btn.url, {
          protocols: ["http", "https"],
          require_protocol: true,
        });
        if (!isValidUrl) {
          toast.error(`Button ${i + 1} has an invalid URL`);
          return false;
        }
      }
      if (btn.type === "Call to Action") {
        if (!btn.phoneNumber?.trim()) {
          toast.error(`Button ${i + 1} phone number is required`);
          return false;
        }
        const e164Regex = /^\+[1-9]\d{6,14}$/;
        if (!e164Regex.test(btn.phoneNumber)) {
          toast.error(`Button ${i + 1} must be a valid phone number with country code`);
          return false;
        }
      }
    }

    return true;
  };

  const handleSave = async () => {
    if (!validateTemplate()) return;

    setLoading(true);
    try {
      // Create gallery
      if (
        uploadedMediaUrl &&
        templateData.headerType &&
        ["Image", "Video", "Document"].includes(templateData.headerType)
      ) {
        const mediaTypeMap: any = {
          Image: "IMAGE",
          Video: "VIDEO",
          Document: "DOCUMENT",
        };

        await dispatch(
          createGalleryPost({
            mediaUrl: uploadedMediaUrl,
            mediaType: mediaTypeMap[templateData.headerType],
            fileName: mediaFile?.name || "file",
            templateName: templateData.templateName,
          })
        ).unwrap();
      }

      // create template
      const dataToSend = {
        ...templateData,
        buttons,
      };

      await dispatch(createWhatsappTemplate(dataToSend)).unwrap();

      toast.success("Template created successfully");
      navigate(-1);
    } catch (error: any) {
      toast.error(error || "Failed to save");
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const getFileAccept = () => {
    switch (templateData.headerType) {
      case "Image":
        return "image/*";

      case "Video":
        return "video/*";

      case "Document":
        return ".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.ppt,.pptx";

      default:
        return "*";
    }
  };


  return (
    <PageLayout
      customNavbar={
        <CustomNavbar>
          <div className="flex gap-3 items-center w-max">
            <IoArrowBack
              className="text-2xl cursor-pointer hover:text-brand-main transition"
              onClick={() => navigate(-1)}
            />
            <span className="font-bold text-lg uppercase">
              CREATE WHATSAPP TEMPLATE
            </span>
          </div>
        </CustomNavbar>
      }
    >
      <div className="rounded-4xl w-full p-2 sm:p-4 bg-gray-100">
        <div className="flex flex-col lg:flex-row gap-3 sm:gap-5 rounded-4xl w-full p-2 sm:p-4 bg-white min-h-screen">

          {/* ================= LEFT SIDE ================= */}
          <div className="flex w-full lg:w-3/5 flex-col gap-3 sm:gap-5">

            {/* Template Details Card */}
            <div className="p-3 sm:p-4 border rounded-tl-4xl rounded-br-4xl rounded-2xl shadow-lg">
              <h2 className="text-lg font-bold text-black mb-3 sm:mb-5">
                Template Details
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">

                <div className="sm:col-span-2 lg:col-span-1">
                  <label className="text-sm font-bold text-text-secondary">
                    Template Name
                  </label>
                  <input
                    type="text"
                    placeholder="Enter the name of the template"
                    value={templateData.templateName}
                    onChange={(e) => handleTemplateNameChange(e.target.value)}
                    className="mt-2 w-full border border-gray-300 rounded-xl p-2 focus:outline-none focus:border-brand-main"
                  />
                </div>

                <div>
                  <label className="text-sm font-bold text-text-secondary">
                    Category
                  </label>
                  <div className="relative mt-2">
                    <select
                      value={templateData.category}
                      onChange={(e) => handleInputChange("category", e.target.value)}
                      className="w-full appearance-none border border-gray-300 rounded-xl px-4 py-2 pr-8 focus:outline-none focus:border-brand-main text-text-secondary"
                    >
                      <option value="">Select</option>
                      <option value="Marketing">Marketing</option>
                      <option value="Utility">Utility</option>
                      <option value="Authentication">Authentication</option>
                    </select>
                    <IoChevronDown className="absolute right-3 top-3 text-gray-400 pointer-events-none" />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-bold text-text-secondary">
                    Language
                  </label>
                  <div className="relative mt-2">
                    <select
                      value={templateData.language}
                      onChange={(e) => handleInputChange("language", e.target.value)}
                      className="w-full appearance-none border border-gray-300 rounded-xl p-2 pr-8 focus:outline-none focus:border-brand-main text-text-secondary"
                    >
                      <option value="">Select</option>
                      <option value="English">English</option>
                    </select>
                    <IoChevronDown className="absolute right-3 top-3 text-gray-400 pointer-events-none" />
                  </div>
                </div>

              </div>
            </div>

            {/* Header + Body Row */}
            <div className="flex flex-col md:flex-row gap-3">

              {/* Header Details */}
              <div className="flex bg-[#F8FFF5] min-h-[220px] p-3 sm:p-4 flex-col gap-2 w-full md:w-1/2 border rounded-tl-4xl rounded-br-4xl rounded-2xl shadow-lg">
                <h2 className="text-lg font-bold text-black mb-3 sm:mb-5">
                  Header Details
                </h2>

                <div>
                  <label className="text-sm font-bold text-text-secondary">
                    Header Type
                  </label>
                  <div className="relative mt-2">
                    <select
                      value={templateData.headerType}
                      onChange={(e) => handleHeaderTypeChange(e.target.value)}
                      className="w-full bg-transparent appearance-none border border-gray-300 rounded-xl px-4 py-2 pr-8 focus:outline-none focus:border-brand-main text-text-secondary"
                    >
                      <option value="">No Header</option>
                      <option value="Text">Text Header</option>
                      <option value="Image">Image Header</option>
                      <option value="Video">Video Header</option>
                      <option value="Document">Document Header</option>
                    </select>
                    <IoChevronDown className="absolute right-3 top-3 text-gray-400 pointer-events-none" />
                  </div>
                </div>

                {/* Text Header Input */}
                {templateData.headerType === "Text" && (
                  <div className="mt-2">
                    <label className="text-sm font-bold text-text-secondary">
                      Header Text
                    </label>
                    <input
                      type="text"
                      placeholder="Enter header text (max 60 characters)"
                      value={templateData.headerText}
                      onChange={(e) =>
                        handleInputChange("headerText", e.target.value.slice(0, 60))
                      }
                      maxLength={60}
                      className="mt-2 w-full bg-transparent border border-gray-300 rounded-xl p-2 focus:outline-none focus:border-brand-main"
                    />
                    {fieldErrors.headerText && (
                      <p className="text-red-500 text-xs mt-1">
                        {fieldErrors.headerText}
                      </p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                      {templateData.headerText?.length || 0}/60 characters
                    </p>
                  </div>
                )}

                {/* Media Upload */}
                {["Image", "Video", "Document"].includes(templateData.headerType) && (
                  <div className="mt-2">
                    <label className="text-sm font-bold text-text-secondary">
                      Upload {templateData.headerType}
                    </label>
                    <input
                      type="file"
                      accept={getFileAccept()}
                      onChange={handleMediaUpload}
                      className="mt-2 w-full text-sm text-gray-500
                        file:mr-3 file:py-1.5 file:px-3
                        file:rounded-lg file:border-0
                        file:text-sm file:font-medium
                        file:bg-brand-main file:text-white
                        hover:file:opacity-90 cursor-pointer"
                    />

                    {uploadingMedia && (
                      <div className="flex items-center gap-2 mt-2">
                        <div className="w-4 h-4 border-2 border-brand-main border-t-transparent rounded-full animate-spin" />
                        <p className="text-xs text-blue-500">Uploading...</p>
                      </div>
                    )}

                    {/* File name for Video/Document */}
                    {mediaFile && templateData.headerType !== "Image" && !uploadingMedia && (
                      <div className="flex items-center gap-2 mt-2">
                        <p className="text-xs text-green-600 flex-1 truncate">
                          ✓ {mediaFile.name}
                        </p>
                        <button
                          onClick={() => {
                            setMediaFile(null);
                            setMediaPreviewUrl("");
                            handleInputChange("mediaHandle", "");
                          }}
                          className="text-red-500 hover:text-red-700"
                        >
                          <IoClose className="text-sm" />
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Body Details */}
              <div className="flex bg-[#F5FDFF] min-h-[220px] p-3 sm:p-4 flex-col gap-2 w-full md:w-1/2 border rounded-tl-4xl rounded-br-4xl rounded-2xl shadow-lg">
                <h2 className="text-lg font-bold text-black mb-3 sm:mb-5">
                  Body Details
                </h2>
                <div className="flex flex-col">
                  <label className="text-sm font-bold text-text-secondary mb-2">
                    Body Text
                  </label>
                  <textarea
                    placeholder="Enter the body text here"
                    value={templateData.bodyText}
                    onChange={(e) => handleInputChange("bodyText", e.target.value)}
                    rows={5}
                    className="w-full resize-none border bg-transparent border-gray-300 rounded-xl px-4 py-3 text-sm
                      focus:outline-none focus:border-brand-main placeholder:text-gray-400"
                  />
                </div>
                {fieldErrors.bodyText && (
                  <p className="text-red-500 text-xs mt-1">
                    {fieldErrors.bodyText}
                  </p>
                )}
              </div>
            </div>

            {/* Footer Details */}
            <div className="bg-[#FFF5FB] p-3 sm:p-4 border rounded-tl-4xl rounded-br-4xl rounded-2xl shadow-lg">
              <h2 className="text-lg font-bold text-black mb-3 sm:mb-5">
                Footer Details (Optional)
              </h2>
              <div className="col-span-1">
                <label className="text-sm font-bold text-text-secondary">
                  Footer Text (max 60 characters)
                </label>
                <input
                  type="text"
                  placeholder="Enter the footer text here"
                  value={templateData.footerText}
                  onChange={(e) =>
                    handleInputChange("footerText", e.target.value.slice(0, 60))
                  }
                  maxLength={60}
                  className="mt-2 w-full bg-transparent border border-gray-300 rounded-xl p-2 focus:outline-none focus:border-brand-main"
                />
                {fieldErrors.footerText && (
                  <p className="text-red-500 text-xs mt-1">
                    {fieldErrors.footerText}
                  </p>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  {templateData.footerText.length}/60 characters
                </p>
              </div>
            </div>

            {/* Buttons Section */}
            <div className="bg-[#FFF5F5] p-3 sm:p-4 border rounded-tl-4xl rounded-br-4xl rounded-2xl shadow-lg">
              <div className="flex justify-between items-center mb-3 sm:mb-5">
                <h2 className="text-lg font-bold text-black">
                  Buttons (Optional)
                </h2>
                <button
                  onClick={addButton}
                  disabled={buttons.length >= 3}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition ${buttons.length >= 3
                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                    : "bg-brand-main text-white hover:opacity-90"
                    }`}
                >
                  <IoAdd className="text-lg" />
                  Add Button
                </button>
              </div>

              {buttons.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">
                  No buttons added yet. Click "Add Button" to add one.
                </p>
              ) : (
                <div className="space-y-3">
                  {buttons.map((button, index) => (
                    <div
                      key={button.id}
                      draggable
                      onDragStart={() => handleDragStart(index)}
                      onDragOver={(e) => handleDragOver(e, index)}
                      onDragEnd={handleDragEnd}
                      className={`p-3 border-2 rounded-xl bg-white transition ${draggedIndex === index
                        ? "border-brand-main opacity-50"
                        : "border-gray-200"
                        }`}
                    >
                      <div className="flex items-center gap-2 mb-3">
                        <IoReorderThree className="text-2xl text-gray-400 cursor-move" />
                        <span className="text-sm font-bold text-text-secondary">
                          Button {index + 1}
                        </span>
                        <button
                          onClick={() => removeButton(button.id)}
                          className="ml-auto text-red-500 hover:text-red-700 transition"
                        >
                          <IoClose className="text-xl" />
                        </button>
                      </div>

                      <div className="space-y-3">
                        {/* Button Type */}
                        <div>
                          <label className="text-sm font-bold text-text-secondary">
                            Button Type
                          </label>
                          <div className="relative mt-2">
                            <select
                              value={button.type}
                              onChange={(e) =>
                                updateButton(button.id, "type", e.target.value)
                              }
                              className="w-full bg-transparent appearance-none border border-gray-300 rounded-xl px-4 py-2 pr-8 focus:outline-none focus:border-brand-main text-text-secondary"
                            >
                              <option value="Quick Reply">Quick Reply</option>
                              <option value="URL">Call to Action (URL)</option>
                              <option value="Call to Action">Call to Action (Phone)</option>
                            </select>
                            <IoChevronDown className="absolute right-3 top-3 text-gray-400 pointer-events-none" />
                          </div>
                        </div>

                        {/* Button Text for URL and Call to Action */}
                        {(button.type === "URL" || button.type === "Call to Action") && (
                          <div>
                            <label className="text-sm font-bold text-text-secondary">
                              Button Text
                            </label>
                            <input
                              type="text"
                              placeholder="Enter button text"
                              value={button.text}
                              maxLength={60}
                              onChange={(e) =>
                                updateButton(
                                  button.id,
                                  "text",
                                  e.target.value.slice(0, 60)
                                )
                              }
                              className="mt-2 bg-transparent w-full border border-gray-300 rounded-xl p-2 focus:outline-none focus:border-brand-main"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              {button.text.length}/60 characters
                            </p>
                          </div>
                        )}

                        {/* Quick Reply Text */}
                        {button.type === "Quick Reply" && (
                          <div>
                            <label className="text-sm font-bold text-text-secondary">
                              Quick Reply Text
                            </label>
                            <input
                              type="text"
                              placeholder="Enter quick reply text"
                              value={button.text}
                              maxLength={60}
                              onChange={(e) =>
                                updateButton(
                                  button.id,
                                  "text",
                                  e.target.value.slice(0, 60)
                                )
                              }
                              className="mt-2 bg-transparent w-full border border-gray-300 rounded-xl p-2 focus:outline-none focus:border-brand-main"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              {button.text.length}/60 characters
                            </p>
                          </div>
                        )}

                        {/* URL Input */}
                        {button.type === "URL" && (
                          <div>
                            <label className="text-sm font-bold text-text-secondary">
                              URL
                            </label>
                            <input
                              type="url"
                              placeholder="Enter URL (e.g., https://example.com)"
                              value={button.url || ""}
                              onChange={(e) =>
                                updateButton(button.id, "url", e.target.value)
                              }
                              className="mt-2 bg-transparent w-full border border-gray-300 rounded-xl p-2 focus:outline-none focus:border-brand-main"
                            />
                            {buttonErrors[button.id] && (
                              <p className="text-red-500 text-xs mt-1">
                                {buttonErrors[button.id]}
                              </p>
                            )}
                          </div>
                        )}

                        {/* Phone Number Input */}
                        {button.type === "Call to Action" && (
                          <div>
                            <label className="text-sm font-bold text-text-secondary">
                              Phone Number
                            </label>
                            <PhoneInput
                              international
                              limitMaxLength
                              defaultCountry="IN"
                              value={button.phoneNumber || ""}
                              onChange={(value) =>
                                updateButton(button.id, "phoneNumber", value || "")
                              }
                              className="mt-2 border border-gray-300 rounded-xl p-2 focus-within:border-brand-main [&_input]:outline-none [&_input]:bg-transparent [&_input]:w-full"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

          </div>

          {/* ================= RIGHT SIDE (Preview) ================= */}
          <div className="flex w-full lg:w-2/5 flex-col">
            <div className="border rounded-3xl p-4 sm:p-5 h-full flex flex-col">
              <h3 className="text-lg font-bold text-black mb-4">
                Live Preview
              </h3>

              <div className="flex-1">
                <WhatsappPreview templateData={templateData} buttons={buttons} mediaPreviewUrl={mediaPreviewUrl} />
              </div>

              <div className="flex justify-end mt-4">
                <button
                  className={`px-6 py-2 bg-brand-blue-gradient text-white font-semibold whitespace-nowrap text-lg
                    rounded-xl shadow hover:opacity-90 transition ${loading ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                  onClick={handleSave}
                  disabled={loading}
                >
                  {loading ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          </div>

        </div>
      </div>
    </PageLayout>
  );
}